import {
  EXPENSE_CATEGORY_LABELS,
  type Equipment,
  Equipment as EquipmentSchema,
  type Expense,
  Expense as ExpenseSchema,
  ExpenseCategory,
  OkResponse,
  PURCHASE_PAYMENT_LABELS,
  PurchasePayment,
  type RecurringExpense,
  RecurringExpense as RecurringSchema,
  type StoredFile,
  addMonthsIso,
  formatBRL,
  formatIsoDate,
  isoDateInSaoPaulo,
  splitInstallments,
} from '@deizi/shared';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { AffixInput, Field, SwitchRow } from '../../components/Form';
import { Modal } from '../../components/Overlays';
import { Alert } from '../../components/States';
import { useToast } from '../../components/Toast';
import { api, newIdempotencyKey } from '../../lib/api';
import { expenseApi, useInvalidateExpenses } from '../../lib/expenses-api';
import { uploadFile } from '../../lib/files-api';
import { errorMessage } from '../../lib/messages';
import { centavosInput, parseBrDate, parseCentavos } from '../../lib/numbers';

export const INSTALLMENT_COUNTS = [1, 2, 3, 4, 6, 10, 12] as const;
const SHORT_MONTHS = [
  'jan',
  'fev',
  'mar',
  'abr',
  'mai',
  'jun',
  'jul',
  'ago',
  'set',
  'out',
  'nov',
  'dez',
];

/** "3 × R$ 96,63 · nov, dez, jan" (months from the first due date). */
export function installmentsText(valueC: number | null, count: number, firstDueOn: string | null) {
  if (!valueC || valueC <= 0) return '—';
  if (count <= 1) return 'à vista';
  const amounts = splitInstallments(valueC, count);
  const each =
    amounts[0] === amounts[1]
      ? `${count} × ${formatBRL(amounts[1]!)}`
      : `${formatBRL(amounts[0]!)} + ${count - 1} × ${formatBRL(amounts[1]!)}`;
  if (!firstDueOn) return each;
  const months = Array.from(
    { length: Math.min(count, 3) },
    (_, i) => SHORT_MONTHS[Number(addMonthsIso(firstDueOn, i).slice(5, 7)) - 1],
  );
  const last = addMonthsIso(firstDueOn, count - 1);
  return `${each} · ${months.join(', ')}${count > 3 ? `… até ${formatIsoDate(last)}` : ''}`;
}

export function monthlyText(valueC: number | null, lifeMonths: number | null) {
  if (!valueC || !lifeMonths) return '—';
  return `${formatBRL(Math.round(valueC / lifeMonths))} por ${lifeMonths} meses`;
}

/** Receipts picker: photo or PDF, uploaded right away. */
export function ReceiptPicker({
  files,
  onAdd,
  onRemove,
  compact,
}: {
  files: StoredFile[];
  onAdd: (f: StoredFile) => void;
  onRemove?: (f: StoredFile) => void;
  compact?: boolean;
}) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({
    mutationFn: (file: File) => uploadFile(file, 'document'),
    onSuccess: onAdd,
    onError: (err) => toast(errorMessage(err)),
  });
  const pick = (
    <>
      <input
        ref={input}
        type="file"
        hidden
        accept="image/*,.pdf"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        className="btn sm"
        disabled={upload.isPending}
        onClick={() => input.current?.click()}
      >
        {upload.isPending ? 'Enviando…' : 'Escolher arquivo'}
      </button>
    </>
  );
  return (
    <div className="col" style={{ gap: 8 }}>
      {files.map((f) => (
        <div
          key={f.id}
          className="card sunk row"
          style={{ gap: 10, padding: 10, borderRadius: 12 }}
        >
          {f.mime.startsWith('image/') ? (
            <img
              src={f.url}
              alt=""
              style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8 }}
            />
          ) : (
            <span className="badge">PDF</span>
          )}
          <a className="grow sm" href={f.url} target="_blank" rel="noreferrer">
            {f.name}
          </a>
          {onRemove ? (
            <button type="button" className="btn sm g" onClick={() => onRemove(f)}>
              Remover
            </button>
          ) : null}
        </div>
      ))}
      {compact ? (
        <div>{pick}</div>
      ) : (
        <div className="dropzone" style={{ padding: 18 }}>
          <span className="sm">Arraste a foto ou o PDF do recibo</span>
          <span className="xs muted">JPG, PNG ou PDF</span>
          {pick}
        </div>
      )}
    </div>
  );
}

function CategorySelect({
  id,
  value,
  onChange,
  disabled,
  options = ExpenseCategory.options,
}: {
  id: string;
  value: ExpenseCategory;
  onChange: (v: ExpenseCategory) => void;
  disabled?: boolean;
  options?: readonly ExpenseCategory[];
}) {
  return (
    <select
      id={id}
      className="input"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(ExpenseCategory.parse(e.target.value))}
    >
      {options.map((c) => (
        <option key={c} value={c}>
          {EXPENSE_CATEGORY_LABELS[c]}
        </option>
      ))}
    </select>
  );
}

export function PaymentSelect({
  id,
  value,
  onChange,
  options = PurchasePayment.options,
}: {
  id: string;
  value: PurchasePayment;
  onChange: (v: PurchasePayment) => void;
  options?: readonly PurchasePayment[];
}) {
  return (
    <select
      id={id}
      className="input"
      value={value}
      onChange={(e) => onChange(PurchasePayment.parse(e.target.value))}
    >
      {options.map((p) => (
        <option key={p} value={p}>
          {PURCHASE_PAYMENT_LABELS[p]}
        </option>
      ))}
    </select>
  );
}

/** Installments + "Custo fixo por mês" sub-card of the equipment forms. */
export function EquipmentFields({
  valueC,
  life,
  setLife,
  count,
  setCount,
  firstDue,
  setFirstDue,
}: {
  valueC: number | null;
  life: string;
  setLife: (v: string) => void;
  count: number;
  setCount: (v: number) => void;
  firstDue: string;
  setFirstDue: (v: string) => void;
}) {
  const lifeN = Number(life);
  const firstIso = parseBrDate(firstDue);
  return (
    <div className="card sunk col" style={{ gap: 10, padding: 14, borderRadius: 14 }}>
      <div className="row wrap" style={{ gap: 12 }}>
        <Field id="eq-life" label="Vida útil" style={{ flex: '1 1 120px' }}>
          <AffixInput
            id="eq-life"
            value={life}
            onChange={setLife}
            unit="meses"
            inputMode="numeric"
            invalid={!(lifeN >= 1)}
          />
        </Field>
        <Field id="eq-parc" label="Parcelas" style={{ flex: '1 1 140px' }}>
          <select
            id="eq-parc"
            className="input"
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
          >
            {INSTALLMENT_COUNTS.map((n) => (
              <option key={n} value={n}>
                {n === 1 ? 'À vista' : `${n}×`}
              </option>
            ))}
          </select>
        </Field>
        {count > 1 ? (
          <Field
            id="eq-first"
            label="1ª parcela em"
            style={{ flex: '1 1 140px' }}
            error={firstIso ? null : 'Use DD/MM/AAAA.'}
          >
            <input
              id="eq-first"
              className="input tab"
              value={firstDue}
              onChange={(e) => setFirstDue(e.target.value)}
            />
          </Field>
        ) : null}
      </div>
      <div className="kv sm">
        <span className="muted">Custo fixo por mês</span>
        <span className="v">{monthlyText(valueC, lifeN >= 1 ? lifeN : null)}</span>
      </div>
      <div className="kv sm">
        <span className="muted">Parcelas</span>
        <span className="v">{installmentsText(valueC, count, firstIso)}</span>
      </div>
    </div>
  );
}

const nextMonthBr = (iso: string) => formatIsoDate(addMonthsIso(iso, 1));

/** "Nova despesa" / "Editar despesa" (board Despesas). */
export function ExpenseDialog({ expense, onClose }: { expense?: Expense; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const today = isoDateInSaoPaulo();
  const [date, setDate] = useState(formatIsoDate(expense?.spentOn ?? today));
  const [value, setValue] = useState(centavosInput(expense?.amountCentavos ?? null));
  const [description, setDescription] = useState(expense?.description ?? '');
  const [category, setCategory] = useState<ExpenseCategory>(expense?.category ?? 'filament');
  const [payment, setPayment] = useState<PurchasePayment>(expense?.paymentMethod ?? 'pix');
  const [supplier, setSupplier] = useState(expense?.supplier ?? '');
  const [notes, setNotes] = useState(expense?.notes ?? '');
  const [files, setFiles] = useState<StoredFile[]>(expense?.files ?? []);
  const [isEquipment, setIsEquipment] = useState(false);
  const [life, setLife] = useState('24');
  const [count, setCount] = useState(1);
  const [firstDue, setFirstDue] = useState(nextMonthBr(today));
  const [tried, setTried] = useState(false);
  const [idem] = useState(newIdempotencyKey);
  const locked = expense?.locked ?? null;
  const valueC = parseCentavos(value);
  const iso = parseBrDate(date);
  const firstIso = parseBrDate(firstDue);
  const valid =
    !!iso &&
    !!valueC &&
    valueC > 0 &&
    description.trim().length > 0 &&
    (!isEquipment || (Number(life) >= 1 && (count === 1 || !!firstIso)));
  const save = useMutation({
    mutationFn: () => {
      if (expense) {
        const base = {
          description: description.trim(),
          paymentMethod: payment,
          supplier: supplier.trim() || null,
          notes: notes.trim() || null,
        };
        return api.patch(
          `/expenses/${expense.id}`,
          ExpenseSchema,
          locked ? base : { ...base, spentOn: iso, amountCentavos: valueC, category },
        );
      }
      return api.post(
        '/expenses',
        ExpenseSchema,
        {
          spentOn: iso,
          description: description.trim(),
          category: isEquipment ? 'equipment' : category,
          amountCentavos: valueC,
          paymentMethod: payment,
          supplier: supplier.trim() || null,
          notes: notes.trim() || null,
          fileIds: files.map((f) => f.id),
          equipment: isEquipment
            ? {
                lifeMonths: Number(life),
                installments: count > 1 && firstIso ? { count, firstDueOn: firstIso } : null,
              }
            : null,
        },
        { idempotencyKey: idem },
      );
    },
    onSuccess: () => {
      invalidate();
      toast(
        expense
          ? 'Despesa atualizada'
          : isEquipment && valueC
            ? `Equipamento lançado: ${formatBRL(Math.round(valueC / Number(life)))} por mês nos custos fixos`
            : `Despesa lançada · ${formatBRL(valueC ?? 0)}`,
      );
      onClose();
    },
  });
  const attach = async (f: StoredFile) => {
    if (!expense) {
      setFiles((x) => [...x, f]);
      return;
    }
    try {
      const e = await expenseApi.attach(expense.id, f.id);
      setFiles(e.files);
      invalidate();
    } catch (err) {
      toast(errorMessage(err));
    }
  };
  return (
    <Modal
      title={expense ? 'Editar despesa' : 'Nova despesa'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => {
              setTried(true);
              if (valid) save.mutate();
            }}
          >
            {expense ? 'Salvar alterações' : 'Lançar despesa'}
          </button>
        </>
      }
    >
      {locked ? <Alert tone="info">{locked} Data, valor e categoria não mudam aqui.</Alert> : null}
      <div className="row wrap" style={{ gap: 12 }}>
        <Field
          id="ex-date"
          label="Data"
          style={{ flex: '1 1 140px' }}
          error={tried && !iso ? 'Use DD/MM/AAAA.' : null}
        >
          <input
            id="ex-date"
            className={locked ? 'input tab readonly' : 'input tab'}
            value={date}
            disabled={!!locked}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        <Field
          id="ex-value"
          label="Valor"
          style={{ flex: '1 1 140px' }}
          error={tried && !(valueC && valueC > 0) ? 'Informe um valor maior que zero.' : null}
        >
          <AffixInput
            id="ex-value"
            prefix="R$"
            placeholder="0,00"
            value={value}
            onChange={setValue}
            disabled={!!locked}
            invalid={tried && !(valueC && valueC > 0)}
          />
        </Field>
      </div>
      <Field
        id="ex-desc"
        label="Descrição"
        error={tried && !description.trim() ? 'Descreva a despesa.' : null}
      >
        <input
          id="ex-desc"
          className="input"
          placeholder="Ex.: 2 carretéis PLA Basic"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <div className="row wrap" style={{ gap: 12 }}>
        <Field id="ex-cat" label="Categoria" style={{ flex: '1 1 160px' }}>
          <CategorySelect
            id="ex-cat"
            value={isEquipment ? 'equipment' : category}
            onChange={setCategory}
            disabled={!!locked || isEquipment || !!expense?.asset}
          />
        </Field>
        <Field id="ex-pay" label="Pagamento" style={{ flex: '1 1 160px' }}>
          <PaymentSelect id="ex-pay" value={payment} onChange={setPayment} />
        </Field>
      </div>
      <Field id="ex-supplier" label="Fornecedor">
        <input
          id="ex-supplier"
          className="input"
          placeholder="opcional"
          value={supplier}
          onChange={(e) => setSupplier(e.target.value)}
        />
      </Field>
      <Field label="Recibo">
        <ReceiptPicker files={files} onAdd={(f) => void attach(f)} compact={files.length > 0} />
      </Field>
      <Field id="ex-notes" label="Notas">
        <textarea
          id="ex-notes"
          className="input"
          rows={2}
          placeholder="opcional"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Field>
      {expense ? null : (
        <>
          <div className="row between">
            <SwitchRow on={isEquipment} onChange={setIsEquipment} label="É equipamento?" />
            <span className="xs muted">dura mais de um ano</span>
          </div>
          {isEquipment ? (
            <EquipmentFields
              valueC={valueC}
              life={life}
              setLife={setLife}
              count={count}
              setCount={setCount}
              firstDue={firstDue}
              setFirstDue={setFirstDue}
            />
          ) : null}
        </>
      )}
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}

/** "Anexar recibo". */
export function AttachDialog({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal title="Anexar recibo" onClose={onClose}>
      <p className="sm muted">
        {expense.description}
        {expense.amountCentavos !== null ? ` · ${formatBRL(expense.amountCentavos)}` : ''}
      </p>
      <ReceiptPicker
        files={[]}
        onAdd={(f) => {
          expenseApi
            .attach(expense.id, f.id)
            .then(() => {
              invalidate();
              toast('Recibo anexado');
              onClose();
            })
            .catch((err: unknown) => setError(errorMessage(err)));
        }}
      />
      {error ? <Alert tone="err">{error}</Alert> : null}
    </Modal>
  );
}

/** "Lançar {item}": confirms the value of an item "a lançar". */
export function LaunchDialog({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const [value, setValue] = useState(centavosInput(expense.amountCentavos));
  const [payment, setPayment] = useState<PurchasePayment>('boleto');
  const [tried, setTried] = useState(false);
  const valueC = parseCentavos(value);
  const ok = !!valueC && valueC > 0;
  const launch = useMutation({
    mutationFn: () =>
      api.post(`/expenses/${expense.id}/launch`, ExpenseSchema, {
        amountCentavos: valueC,
        paymentMethod: payment,
      }),
    onSuccess: () => {
      invalidate();
      toast(`${expense.description} lançado · ${formatBRL(valueC ?? 0)}`);
      onClose();
    },
  });
  return (
    <Modal
      title={`Lançar ${expense.description}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={launch.isPending}
            onClick={() => {
              setTried(true);
              if (ok) launch.mutate();
            }}
          >
            Lançar despesa
          </button>
        </>
      }
    >
      <p className="sm">
        {expense.category === 'taxes'
          ? 'Confira o valor no boleto do DAS (Portal do Empreendedor).'
          : 'Confira o valor da conta deste mês.'}{' '}
        Vence em {formatIsoDate(expense.spentOn)}.
      </p>
      <div className="row wrap" style={{ gap: 12 }}>
        <Field
          id="la-value"
          label="Valor"
          style={{ flex: '1 1 140px' }}
          error={tried && !ok ? 'Informe o valor.' : null}
        >
          <AffixInput
            id="la-value"
            prefix="R$"
            value={value}
            onChange={setValue}
            invalid={tried && !ok}
          />
        </Field>
        <Field id="la-pay" label="Pagamento" style={{ flex: '1 1 140px' }}>
          <PaymentSelect id="la-pay" value={payment} onChange={setPayment} />
        </Field>
      </div>
      {launch.isError ? <Alert tone="err">{errorMessage(launch.error)}</Alert> : null}
    </Modal>
  );
}

/** "Excluir esta despesa?" with "Desfazer" in the toast. */
export function DeleteDialog({ expense, onClose }: { expense: Expense; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const remove = useMutation({
    mutationFn: () => expenseApi.remove(expense.id),
    onSuccess: () => {
      invalidate();
      toast('Despesa movida para a Lixeira', {
        label: 'Desfazer',
        onClick: () =>
          void expenseApi
            .restore(expense.id)
            .then(invalidate)
            .catch((err: unknown) => toast(errorMessage(err))),
      });
      onClose();
    },
  });
  return (
    <Modal
      title="Excluir esta despesa?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn d"
            disabled={remove.isPending}
            onClick={() => remove.mutate()}
          >
            Mover para a Lixeira
          </button>
        </>
      }
    >
      <p>
        <b>{expense.description}</b>
        {expense.amountCentavos !== null ? ` · ${formatBRL(expense.amountCentavos)}` : ''}
      </p>
      <p className="sm muted">
        Ela vai para a Lixeira (em Dados) e fica lá por 30 dias. O relatório do mês é recalculado.
        {expense.asset ? ' O equipamento sai junto.' : ''}
      </p>
      {remove.isError ? <Alert tone="err">{errorMessage(remove.error)}</Alert> : null}
    </Modal>
  );
}

/** "Nova despesa recorrente" / edit. */
export function RecurringDialog({
  recurring,
  onClose,
}: {
  recurring?: RecurringExpense;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const [name, setName] = useState(recurring?.name ?? '');
  const [value, setValue] = useState(centavosInput(recurring?.amountCentavos ?? null));
  const [day, setDay] = useState(String(recurring?.day ?? 10));
  const [category, setCategory] = useState<ExpenseCategory>(recurring?.category ?? 'software');
  const [tried, setTried] = useState(false);
  const [idem] = useState(newIdempotencyKey);
  const dayN = Math.min(28, Math.max(1, Math.round(Number(day) || 1)));
  const valueC = value.trim() ? parseCentavos(value) : null;
  const ok = name.trim().length > 0 && (value.trim() === '' || (!!valueC && valueC > 0));
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        category,
        day: dayN,
        amountCentavos: valueC && valueC > 0 ? valueC : null,
      };
      return recurring
        ? api.patch(`/recurring-expenses/${recurring.id}`, RecurringSchema, body)
        : api.post('/recurring-expenses', RecurringSchema, body, { idempotencyKey: idem });
    },
    onSuccess: () => {
      invalidate();
      toast(recurring ? 'Recorrente atualizada' : `Recorrente criada · todo dia ${dayN}`);
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/recurring-expenses/${recurring!.id}`, OkResponse),
    onSuccess: () => {
      invalidate();
      toast(`${recurring!.name} removida. As despesas já lançadas ficam.`);
      onClose();
    },
  });
  return (
    <Modal
      title={recurring ? 'Editar recorrente' : 'Nova despesa recorrente'}
      onClose={onClose}
      footer={
        <>
          {recurring ? (
            <button
              type="button"
              className="btn d"
              style={{ marginRight: 'auto' }}
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Excluir
            </button>
          ) : null}
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => {
              setTried(true);
              if (ok) save.mutate();
            }}
          >
            Salvar recorrente
          </button>
        </>
      }
    >
      <Field id="rc-name" label="Nome" error={tried && !name.trim() ? 'Dê um nome.' : null}>
        <input
          id="rc-name"
          className="input"
          placeholder="Ex.: Assinatura do fatiador"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <div className="row wrap" style={{ gap: 12 }}>
        <Field id="rc-value" label="Valor" style={{ flex: '1 1 140px' }}>
          <AffixInput
            id="rc-value"
            prefix="R$"
            placeholder="variável"
            value={value}
            onChange={setValue}
            invalid={tried && !ok && value.trim() !== ''}
          />
        </Field>
        <Field
          id="rc-day"
          label="Dia do mês"
          style={{ flex: '1 1 100px' }}
          help="de 1 a 28 (todo mês tem)"
        >
          <input
            id="rc-day"
            className="input tab"
            inputMode="numeric"
            value={day}
            onChange={(e) => setDay(e.target.value)}
          />
        </Field>
        <Field id="rc-cat" label="Categoria" style={{ flex: '1 1 160px' }}>
          <CategorySelect id="rc-cat" value={category} onChange={setCategory} />
        </Field>
      </div>
      <p className="xs muted">
        Sem valor = valor variável: aparece como “a lançar” e você confirma no dia. Recorrentes
        entram nos custos fixos do mês.
      </p>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
      {remove.isError ? <Alert tone="err">{errorMessage(remove.error)}</Alert> : null}
    </Modal>
  );
}

/** "Novo equipamento" / edit (Admin). */
export function EquipmentDialog({
  equipment,
  onClose,
}: {
  equipment?: Equipment;
  onClose: () => void;
}) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const today = isoDateInSaoPaulo();
  const [name, setName] = useState(equipment?.name ?? '');
  const [value, setValue] = useState(centavosInput(equipment?.valueCentavos ?? null));
  const [date, setDate] = useState(formatIsoDate(equipment?.purchasedOn ?? today));
  const [life, setLife] = useState(String(equipment?.lifeMonths ?? 24));
  const [count, setCount] = useState(1);
  const [firstDue, setFirstDue] = useState(nextMonthBr(today));
  const [payment, setPayment] = useState<PurchasePayment>('credit_card');
  const [tried, setTried] = useState(false);
  const [idem] = useState(newIdempotencyKey);
  const valueC = parseCentavos(value);
  const iso = parseBrDate(date);
  const ok =
    name.trim().length > 0 &&
    !!valueC &&
    valueC > 0 &&
    !!iso &&
    Number(life) >= 1 &&
    (count === 1 || !!parseBrDate(firstDue));
  const save = useMutation({
    mutationFn: () => {
      const base = {
        name: name.trim(),
        valueCentavos: valueC,
        purchasedOn: iso,
        lifeMonths: Number(life),
      };
      if (equipment) return api.patch(`/equipment/${equipment.id}`, EquipmentSchema, base);
      const first = parseBrDate(firstDue);
      return api.post(
        '/equipment',
        EquipmentSchema,
        {
          ...base,
          paymentMethod: payment,
          installments: count > 1 && first ? { count, firstDueOn: first } : null,
        },
        { idempotencyKey: idem },
      );
    },
    onSuccess: (e) => {
      invalidate();
      toast(
        equipment
          ? 'Equipamento atualizado'
          : `Equipamento cadastrado · ${formatBRL(e.monthlyCostCentavos ?? 0)} por mês`,
      );
      onClose();
    },
  });
  return (
    <Modal
      title={equipment ? `Editar ${equipment.name}` : 'Novo equipamento'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={save.isPending}
            onClick={() => {
              setTried(true);
              if (ok) save.mutate();
            }}
          >
            Salvar equipamento
          </button>
        </>
      }
    >
      <Field id="eq-name" label="Nome" error={tried && !name.trim() ? 'Dê um nome.' : null}>
        <input
          id="eq-name"
          className="input"
          placeholder="Ex.: Lixadeira orbital"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <div className="row wrap" style={{ gap: 12 }}>
        <Field
          id="eq-value"
          label="Valor"
          style={{ flex: '1 1 140px' }}
          error={tried && !(valueC && valueC > 0) ? 'Informe um valor maior que zero.' : null}
        >
          <AffixInput
            id="eq-value"
            prefix="R$"
            placeholder="0,00"
            value={value}
            onChange={setValue}
            invalid={tried && !(valueC && valueC > 0)}
          />
        </Field>
        <Field
          id="eq-date"
          label="Data da compra"
          style={{ flex: '1 1 140px' }}
          error={tried && !iso ? 'Use DD/MM/AAAA.' : null}
        >
          <input
            id="eq-date"
            className="input tab"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Field>
        {equipment ? null : (
          <Field id="eq-pay" label="Pagamento" style={{ flex: '1 1 160px' }}>
            <PaymentSelect id="eq-pay" value={payment} onChange={setPayment} />
          </Field>
        )}
      </div>
      {equipment ? (
        <Field id="eq-life" label="Vida útil">
          <AffixInput
            id="eq-life"
            value={life}
            onChange={setLife}
            unit="meses"
            inputMode="numeric"
          />
        </Field>
      ) : (
        <EquipmentFields
          valueC={valueC}
          life={life}
          setLife={setLife}
          count={count}
          setCount={setCount}
          firstDue={firstDue}
          setFirstDue={setFirstDue}
        />
      )}
      <p className="xs muted">
        Impressoras depreciam por hora impressa: cadastre em Impressoras para entrar no custo de
        cada peça.
      </p>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}

/** "Plano de parcelas" (Admin): count, first due date and total. */
export function PlanDialog({ equipment, onClose }: { equipment: Equipment; onClose: () => void }) {
  const toast = useToast();
  const invalidate = useInvalidateExpenses();
  const current = equipment.installments;
  const [count, setCount] = useState(String(current.length || 10));
  const [firstDue, setFirstDue] = useState(
    formatIsoDate(current[0]?.dueOn ?? addMonthsIso(isoDateInSaoPaulo(), 1)),
  );
  const [total, setTotal] = useState(
    centavosInput(current.reduce((n, i) => n + i.amountCentavos, 0) || equipment.valueCentavos),
  );
  const countN = Math.round(Number(count));
  const firstIso = parseBrDate(firstDue);
  const totalC = parseCentavos(total);
  const ok = countN >= 1 && countN <= 60 && !!firstIso && !!totalC && totalC > 0;
  const save = useMutation({
    mutationFn: () =>
      api.post(`/equipment/${equipment.id}/installments`, EquipmentSchema, {
        count: countN,
        firstDueOn: firstIso,
        totalCentavos: totalC,
      }),
    onSuccess: () => {
      invalidate();
      toast(countN > 1 ? `Plano salvo · ${countN} parcelas` : 'Plano removido: à vista');
      onClose();
    },
  });
  return (
    <Modal
      title={`Parcelas · ${equipment.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn p"
            disabled={!ok || save.isPending}
            onClick={() => save.mutate()}
          >
            Salvar plano
          </button>
        </>
      }
    >
      <div className="row wrap" style={{ gap: 12 }}>
        <Field id="pl-count" label="Parcelas" style={{ flex: '1 1 100px' }}>
          <input
            id="pl-count"
            className="input tab"
            inputMode="numeric"
            value={count}
            onChange={(e) => setCount(e.target.value)}
          />
        </Field>
        <Field
          id="pl-first"
          label="1ª parcela em"
          style={{ flex: '1 1 140px' }}
          error={firstIso ? null : 'Use DD/MM/AAAA.'}
        >
          <input
            id="pl-first"
            className="input tab"
            value={firstDue}
            onChange={(e) => setFirstDue(e.target.value)}
          />
        </Field>
        <Field id="pl-total" label="Total parcelado" style={{ flex: '1 1 160px' }}>
          <AffixInput id="pl-total" prefix="R$" value={total} onChange={setTotal} />
        </Field>
      </div>
      <div className="kv sm">
        <span className="muted">Parcelas</span>
        <span className="v">{installmentsText(totalC, countN, firstIso)}</span>
      </div>
      <p className="xs muted">
        Parcela é caixa, depreciação é custo: as parcelas aparecem no caixa do mês e nunca entram no
        lucro.
      </p>
      {save.isError ? <Alert tone="err">{errorMessage(save.error)}</Alert> : null}
    </Modal>
  );
}
